import React, { createContext, useContext, useEffect } from 'react';
import type { StreamAskArgs } from '../../../lib/askStream';
import {
  useAgentAsk,
  type AgentActivityState,
} from '../hooks/useAgentAsk';
import type { AgentProgressEvent, AskResponse } from '../types';

type AgentAskContextValue = {
  ask: (
    args: StreamAskArgs,
    opts?: { onProgress?: (event: AgentProgressEvent) => void },
  ) => Promise<AskResponse>;
  busy: boolean;
  activity: AgentActivityState;
  cancel: () => void;
  resetActivity: () => void;
  isInFlight: () => boolean;
};

const AgentAskContext = createContext<AgentAskContextValue | null>(null);

/** Keeps the SSE connection alive while agent panels are closed. */
export function AgentAskProvider({ children }: { children: React.ReactNode }) {
  const agent = useAgentAsk();

  useEffect(() => {
    return () => agent.cancel();
  }, [agent.cancel]);

  return (
    <AgentAskContext.Provider value={agent}>{children}</AgentAskContext.Provider>
  );
}

export function useAgentAskContext(): AgentAskContextValue {
  const ctx = useContext(AgentAskContext);
  if (!ctx) {
    throw new Error('useAgentAskContext must be used within AgentAskProvider');
  }
  return ctx;
}
